// @flow
import React from 'react';
import { get, size, omit, trim } from 'lodash';
import classNames from 'classnames';
import { SettingsMenu, FormFooter, PlatformSwitch, AppMobileHeader, AppMobileFooter } from '@blocks';
import { Input, InputMask, Breadcrumb, Toggle } from '@elements';
import type { User, FormElementData, BundledPermissions } from '@types';
import { createValidator } from '@helpers/validator';
import { withForm } from '@hocs';
import { withTranslation } from 'react-i18next';
import { createNsTranslator, handleSubmitEvent, keepKeysRef, UserNotifications } from '@helpers/helpers';
import ServiceService from '../../../../services/ServiceService';
import './ServiceTemplateForm.scss';

type State = {
  errors: Object,
};

type Props = {
  user: User,
  onSave: Function,
  onDelete?: Function,
  isEdit?: boolean, // the edit route, which may open before its service is loaded
  doctorId: number,
  user: User,
  onSave: Function,
  onLogout: Function,
  handleFormChange: Function,
  handleTouchField: Function,
  bindValidation: Function,
  formData: Object,
  touchedFields: Object,
  changedFields: Object,
  initialData: Object,
  permissions: BundledPermissions,
  t: Function,
  onCancel: Function,
  formEdited: Function,
  isSaving: boolean,
};

const validator = createValidator()
  .field('name')
  .present('Informe um nome')
  .field('price')
  .minNumber(0.0, 'Informe um valor');

const UNIT_MAX_LENGTH = 20;

class ServiceTemplateForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      errors: {},
    };
    this.props.bindValidation(this.handleValidation);
  }

  componentDidMount() {
    handleSubmitEvent('.serviceTemplateForm', () => {
      if (this.props.formEdited()) {
        this.onSaveService();
      }
    });
  }

  render() {
    const {
      name, price, healthInsurance, unit,
    } = this.props.formData;
    const {
      onDelete, user, doctorId, handleFormChange, handleTouchField,
      onLogout, isSaving,
    } = this.props;

    const _ = createNsTranslator('settings', this.props.t);
    const byQuantity = this.isByQuantity();

    let priceLabel = _('label/value', 'global');
    if (byQuantity) {
      priceLabel = trim(unit)
        ? this.props.t('settings:label/services/form/unit_value', { unit: trim(unit) })
        : _('label/services/form/unit_value_default');
    }

    const { isEdit } = this.props;
    const title = isEdit ? _('label/edit_service', 'financial') : _('label/services/form/new_service');

    const breadcrumbLinks = [
      { text: _('label/services/form/service_value'), linkTo: `/ajustes/servicos/${doctorId}` },
      { text: isEdit ? title : _('label/services/add_service') },
    ];

    const pageContent = (
      <React.Fragment>
        <Input
          type="text"
          name="name"
          autofocus
          mobileAutofocus={!get(this.props.formData, 'id')}
          className="field"
          label={_('label/name', 'global')}
          required
          value={name}
          onChange={handleFormChange}
          onBlur={handleTouchField}
          error={get(this.state.errors, 'name', null)}
        />
        {this.clinicSellsByQuantity() && (
          <div className="byQuantityFields">
            <Toggle
              name="byQuantity"
              label={_('label/services/form/by_quantity')}
              onChange={handleFormChange}
              isChecked={byQuantity}
            />
            <p className="byQuantityHint">{_('label/services/form/by_quantity_hint')}</p>
            {byQuantity && (
              <React.Fragment>
                <Input
                  type="text"
                  name="unit"
                  className="field"
                  label={_('label/services/form/unit')}
                  required
                  value={unit || ''}
                  onChange={handleFormChange}
                  onBlur={handleTouchField}
                  error={get(this.state.errors, 'unit', null)}
                />
                <div className="unitSuggestions" ref={this.bindUnitSuggestions}>
                  {_('label/services/form/unit_suggestions').split(',').map(suggestion => (
                    <button
                      type="button"
                      key={suggestion}
                      className={classNames('unitSuggestion', { active: trim(unit) === suggestion })}
                      onClick={() => handleFormChange({ name: 'unit', value: suggestion })}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </React.Fragment>
            )}
          </div>
        )}
        <InputMask
          type="currency"
          name="price"
          label={priceLabel}
          className="field"
          required
          value={price}
          onChange={handleFormChange || 0}
          onBlur={handleTouchField}
          error={get(this.state.errors, 'price', null)}
        />
        <br />
        <Toggle
          name="healthInsurance"
          key="toggleSwitch"
          label={_('label/status_health_insurance', 'global')}
          onChange={handleFormChange}
          isChecked={healthInsurance}
          error={get(this.state.errors, 'healthInsurance', null)}
        />
        <FormFooter
          onSave={this.onSaveService}
          onDelete={onDelete}
          onCancel={this.handleCancel}
          isLoading={isSaving}
        />
      </React.Fragment>
    );

    return (
      <PlatformSwitch
        inject
        desktop={() => (
          <div className="appContent settingsPage">
            <aside className="sidebar">
              <SettingsMenu user={user} onLogout={onLogout} permissions={this.props.permissions} />
            </aside>
            <section className="mainSection">
              <div className="centerContent serviceTemplateForm">
                <Breadcrumb items={breadcrumbLinks} />
                <div className="toolbar dashed">
                  <h1>{title}</h1>
                </div>
                {pageContent}
              </div>
            </section>
          </div>
        )}
        mobile={() => (
          <React.Fragment>
            <AppMobileHeader title={title} onBackClick={this.handleCancel} />
            <div className="appContent settingsPage">
              <section className="mainSection">
                <div className="centerContent">{pageContent}</div>
              </section>
            </div>
            <AppMobileFooter />
          </React.Fragment>
        )}
      />
    );
  }

  onSaveService = () => this.handleValidation(true).then(valid => {
    if (valid) {
      const { onSave, formData } = this.props;
      const data = omit(formData, 'byQuantity');
      // without the flag the unit is left as it is, so turning the flag off keeps existing units
      if (this.clinicSellsByQuantity()) {
        data.unit = this.isByQuantity() ? trim(formData.unit) : null;
      }
      onSave(data);
    }
  });

  clinicSellsByQuantity = () => !!get(this.props.permissions, ['permissionTo', 'servicesByQuantity']);

  // Until the toggle is touched, the template as it was loaded decides: clearing the unit to retype it keeps the field
  isByQuantity = () => this.clinicSellsByQuantity() && get(this.props.formData, 'byQuantity', ServiceService.isTemplateByQuantity(this.props.initialData));

  // Enter on a suggestion would save the form (handleSubmitEvent) before the click sets the unit
  bindUnitSuggestions = keepKeysRef((event) => event.key === 'Enter');

  unitError = () => {
    if (!this.isByQuantity()) return null;
    const _ = createNsTranslator('settings', this.props.t);
    const unit = trim(get(this.props.formData, 'unit'));
    if (!unit) return _('label/services/form/error/unit');
    if (unit.length > UNIT_MAX_LENGTH) return _('label/services/form/error/unit_length');
    return null;
  };

  handleCancel = () => {
    if (!size(this.props.changedFields)) {
      this.props.onCancel();
    } else {
      UserNotifications.confirmI18n('alert/confirm_quit_form', null, null, true)
        .then((value) => {
          if (value) {
            this.props.onCancel();
          }
        });
    }
  };

  handleValidation = (isSubmit: boolean = false): Promise<boolean> => new Promise((resolve: Function) => {
    const { formData, touchedFields } = this.props;
    const result = validator.validate(formData, touchedFields, isSubmit);
    const unitError = this.unitError();
    const errors = { ...result.errors };
    if (unitError && (touchedFields.unit || isSubmit)) {
      errors.unit = unitError;
    }
    this.setState({ errors }, () => resolve(result.valid && !unitError));
  });


  handleFormChange = (data: FormElementData) => {
    const { name, value } = data;
    this.setState({ [name]: value });
  };
}

const initialData = {
  price: 0,
};

export default withTranslation()(withForm(initialData)(ServiceTemplateForm));
